import React, { FC, useContext } from "react";
import styled, { ThemeContext } from "styled-components";
import { Button } from "../../button/Button";
import { Divider } from "../../divider/Divider";
import { Heading } from "../../heading/Heading";
import { CartFragment } from "../../../graphql/generated";
import { getTotalSavings } from "../common/helpers";
import {
  convertFromCentsToDollars,
  formatDollars,
} from "../../../helpers/currency";
import { Device } from "../../../helpers/screenSizes";
import { colors } from "../../../styles/variables";

const StyledSummary = styled.div`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius || "3px"};
  border: 1px solid
    ${({ theme }) => theme.cart?.sectionBackgroundColor || colors.gray};
  background-color: ${({ theme }) =>
    theme.cart?.sectionBackgroundColor || "transparent"};

  @media ${Device.mobile} {
    padding: 20px 0 0 0;
    border: none;
  }
`;

const InnerWrapper = styled.div`
  padding: 20px;
`;

const StyledHeading = styled(Heading)`
  padding-bottom: 10px;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;

  &:first-of-type {
    @media ${Device.mobile} {
      padding-top: 20px;
    }
  }
`;

const StyledDivider = styled(Divider)`
  margin: 10px 0;
`;

const BoldText = styled.div`
  font-weight: 700;
`;

const ThemedText = styled.div`
  color: ${({ theme }) => theme.priceAccentColor || theme.color.action};
  font-weight: 500;
`;

const ButtonWrapper = styled.div`
  padding-top: 10px;

  @media ${Device.mobile} {
    padding-top: 15px;
  }
`;

export interface CartSummaryProps {
  cart: CartFragment;
  subtotal: number;
  total: number;
  shippingFee: number;
  isButtonDisabled: boolean;
  goToCheckout: () => void;
}

export const CartSummary: FC<CartSummaryProps> = ({
  cart,
  subtotal,
  total,
  shippingFee,
  isButtonDisabled,
  goToCheckout,
}) => {
  const theme = useContext(ThemeContext);

  const savings = getTotalSavings(cart);

  return (
    <StyledSummary>
      <InnerWrapper>
        <StyledHeading level="h3" size="xs">
          Order Summary
        </StyledHeading>

        <StyledDivider />

        <SummaryItem data-testid="summary-subtotal">
          <div>Subtotal</div>
          <div>{convertFromCentsToDollars(subtotal)}</div>
        </SummaryItem>

        <SummaryItem>
          <div>Shipping</div>
          <div>
            {shippingFee === 0 ? (
              <ThemedText>Free</ThemedText>
            ) : (
              <div>{convertFromCentsToDollars(shippingFee)}</div>
            )}
          </div>
        </SummaryItem>

        <StyledDivider />

        <SummaryItem>
          <BoldText>Grand Total</BoldText>
          <BoldText>{convertFromCentsToDollars(total)}</BoldText>
        </SummaryItem>

        {savings > 0 && (
          <SummaryItem>
            <span></span>
            <ThemedText>You Save {formatDollars(savings)}</ThemedText>
          </SummaryItem>
        )}

        <ButtonWrapper>
          <Button
            data-testid="proceed-checkout-button"
            type="secondary"
            fullWidth
            onClick={goToCheckout}
            disabled={isButtonDisabled}
            fontSize={theme.cart?.buttonFontSize}
          >
            Proceed To Checkout
          </Button>
        </ButtonWrapper>
      </InnerWrapper>
    </StyledSummary>
  );
};
